import { Fragment, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import { Modal, Button, Dropdown } from 'components-antd';
import { InputLabel, Input } from 'components';
import { TextArea } from 'components-antd';
import { Checkmark, ArrowDown, ArrowUp, Lock } from 'components/Icons';
import { UsersIcon } from 'pages/Intel/IntelAgent/components/Icons';
import { Icons } from './../../Icons';

import { createTransactionLink, updateTransactionLink } from 'api/transactions';
import { showSuccessMessage } from 'helpers';
import { isValidURL } from 'utils';

import styles from './styles.module.scss';

const initialState = {
  link: '',
  name: '',
  description: '',
  access: 'Everybody',
};

export const accessOptions = [
  {
    key: 1,
    label: 'Just Me',
    icon: <Icons variant={Icons.USER} />,
  },
  {
    key: 2,
    label: 'Internal',
    icon: <Lock />,
  },
  {
    key: 3,
    label: 'Internal + Client(s)',
    icon: <UsersIcon className={styles.usersIcon} />,
  },
  {
    key: 4,
    label: 'Everybody',
    icon: <Icons variant={Icons.EVERYONE} />,
  },
];

export const LinkModal = ({ trxId, trxLinkData, open, closeModal, fetchTransactionLinks }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showErrorText, setShowErrorText] = useState(false);

  const [data, setData] = useState<any>(initialState);

  useEffect(() => {
    setData(
      trxLinkData
        ? {
            link: trxLinkData?.Address,
            name: trxLinkData?.Name,
            description: trxLinkData?.Description,
            access: trxLinkData?.Access,
          }
        : initialState,
    );
  }, [trxLinkData]);

  const TextLabel = ({ label, icon }) => (
    <div
      className={styles.item}
      onClick={() => {
        setData({ ...data, access: label });
        setShowDropdown(false);
      }}
    >
      <div className={styles.labelLeft}>
        {icon}
        <span>{label}</span>
      </div>
      {data?.access === label && <Checkmark color="#04a451" />}
    </div>
  );

  const handleChange = (e) => setData({ ...data, [e.target.name]: e.target.value });

  const onCloseModal = () => {
    setShowDropdown(false);
    setData(initialState);
    closeModal();
  };

  const onSaveLink = async () => {
    const { link, name, description, access } = data;
    if (!isValidURL(link)) return;

    setLoading(true);

    try {
      const payload = {
        id: trxId,
        Address: link,
        Name: name,
        Description: description,
        Access: access,
      };
      const response = trxLinkData
        ? await updateTransactionLink({ ...payload, linkId: trxLinkData?.Id })
        : await createTransactionLink(payload);

      if (response.status === 200) {
        showSuccessMessage(`Link ${trxLinkData ? 'updated' : 'created'} successfully`);
        fetchTransactionLinks();
        onCloseModal();
      }
    } catch (err) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const ErrorText = () => <p className={styles.errorText}>* Please enter a valid link.</p>;

  const isBtnDisable = useMemo(
    () => !isValidURL(data?.link) || !data?.name,
    [data?.link, data?.name, showErrorText],
  );

  return (
    <Modal
      open={open}
      width={675}
      onCancel={onCloseModal}
      className={styles.trxLinkModal}
      footer={null}
    >
      <p className={styles.header}>New Link</p>
      <div className={styles.content}>
        <p className={styles.detailsText}>Details</p>
        <div className={styles.formFields}>
          <div className={styles.formRow}>
            <InputLabel label="Link Address" showAsterisk />
            <Input
              name="link"
              variant={Input.LIGHT_ROUND}
              placeholder="Enter link address"
              value={data?.link}
              onChange={handleChange}
              onBlur={() => setShowErrorText(!isValidURL(data?.link))}
              maxLength={255}
            />
            {showErrorText && <ErrorText />}
          </div>
          <div className={styles.formRow}>
            <InputLabel label="Name" showAsterisk />
            <Input
              name="name"
              variant={Input.LIGHT_ROUND}
              placeholder="Enter link name"
              value={data?.name}
              onChange={handleChange}
              maxLength={255}
            />
          </div>
          <div className={styles.formRow}>
            <InputLabel label="Description" showOptional />
            <TextArea
              name="description"
              placeholder="Enter link description"
              value={data?.description}
              variant={TextArea.LIGHT_ROUNDED}
              rows={3}
              onChange={handleChange}
              maxLength={255}
            />
          </div>
          <div className={styles.formRow}>
            <InputLabel label="Access" />
            <Dropdown
              trigger={['click']}
              overlay={
                <div className={styles.accessDropdown}>
                  {accessOptions?.map((item) => (
                    <TextLabel key={item.key} label={item?.label} icon={item?.icon} />
                  ))}
                </div>
              }
              open={showDropdown}
              placement="bottom"
            >
              <Button
                variant="hollow-bordered"
                className={styles.dropdownBtn}
                onClick={() => setShowDropdown(!showDropdown)}
              >
                <span>{data?.access}</span>
                {showDropdown ? <ArrowUp /> : <ArrowDown />}
              </Button>
            </Dropdown>
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <Button
          variant="secondary"
          className={classNames(styles.createBtn, { [styles.disabled]: isBtnDisable })}
          disabled={isBtnDisable}
          loading={loading}
          onClick={onSaveLink}
        >
          {trxLinkData ? 'Update' : 'Create'}
        </Button>
      </div>
    </Modal>
  );
};
